import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Clock, LogIn, LogOut, Calendar, Users, ClipboardCheck, Gift, Plus, Trash2, Check, X, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import SearchableSelect from '@/components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const subTabs = [
  { id: 'daily', label: 'Daily Attendance', icon: Clock },
  { id: 'regularize', label: 'Regularization', icon: ClipboardCheck },
  { id: 'team', label: 'Team View', icon: Users, roles: ['admin', 'team_leader'] },
  { id: 'holidays', label: 'Holidays', icon: Calendar },
  { id: 'records', label: 'My Records', icon: FileText },
  { id: 'compoff', label: 'Comp Off', icon: Gift },
];

export default function Attendance() {
  const [activeTab, setActiveTab] = useState('daily');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const ud = localStorage.getItem('user');
    if (ud) setUser(JSON.parse(ud));
  }, []);

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const isManagement = user && (user.role === 'admin' || user.role === 'team_leader');
  const isAdmin = user && user.role === 'admin';

  const visibleTabs = subTabs.filter(t => !t.roles || (user && t.roles.includes(user.role)));

  return (
    <div data-testid="attendance-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-1" data-testid="attendance-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Attendance</h1>
        <p className="text-slate-500 text-sm">Track attendance, regularize, holidays & comp-off</p>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-6 flex-wrap" data-testid="attendance-tabs">
        {visibleTabs.map(t => (
          <button
            key={t.id}
            data-testid={`att-tab-${t.id}`}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-all ${activeTab === t.id ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500 hover:text-blue-700'}`}
          >
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {activeTab === 'daily' && <DailyAttendance getHeaders={getHeaders} isManagement={isManagement} user={user} />}
      {activeTab === 'regularize' && <Regularization getHeaders={getHeaders} isManagement={isManagement} user={user} />}
      {activeTab === 'team' && <TeamView getHeaders={getHeaders} user={user} />}
      {activeTab === 'holidays' && <Holidays getHeaders={getHeaders} isAdmin={isAdmin} />}
      {activeTab === 'records' && <EmployeeRecords getHeaders={getHeaders} user={user} isManagement={isManagement} />}
      {activeTab === 'compoff' && <CompOff getHeaders={getHeaders} isManagement={isManagement} user={user} />}
    </div>
  );
}

/* ==================== DAILY ATTENDANCE ==================== */
function DailyAttendance({ getHeaders, isManagement, user }) {
  const [todayRecord, setTodayRecord] = useState(null);
  const [records, setRecords] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [filterUser, setFilterUser] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchText, setSearchText] = useState('');

  const fetchToday = useCallback(async () => {
    try { const res = await axios.get(`${API}/hrms/attendance/today`, { headers: getHeaders() }); setTodayRecord(res.data); } catch (err) { console.error("./pages/Attendance.jsx:", err); }
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      let url = `${API}/hrms/attendance`;
      const params = [];
      if (filterUser) params.push(`user_id=${filterUser}`);
      if (filterDateFrom) params.push(`date_from=${filterDateFrom}`);
      if (filterDateTo) params.push(`date_to=${filterDateTo}`);
      if (params.length) url += `?${params.join('&')}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setRecords(res.data);
    } catch (e) { toast.error('Failed to fetch attendance'); }
  }, [filterUser, filterDateFrom, filterDateTo]);

  const fetchUsers = useCallback(async () => {
    try { const res = await axios.get(`${API}/users`, { headers: getHeaders() }); setAllUsers(res.data); } catch (err) { console.error("./pages/Attendance.jsx:", err); }
  }, []);

  useEffect(() => { fetchToday(); fetchRecords(); if (isManagement) fetchUsers(); }, [user]);
  useEffect(() => { fetchRecords(); }, [filterUser, filterDateFrom, filterDateTo]);

  const handleCheckIn = async () => {
    try { await axios.post(`${API}/hrms/attendance/check-in`, {}, { headers: getHeaders() }); toast.success('Checked in!'); fetchToday(); fetchRecords(); } catch (e) { toast.error(e.response?.data?.detail || 'Check-in failed'); }
  };
  const handleCheckOut = async () => {
    try { await axios.post(`${API}/hrms/attendance/check-out`, {}, { headers: getHeaders() }); toast.success('Checked out!'); fetchToday(); fetchRecords(); } catch (e) { toast.error(e.response?.data?.detail || 'Check-out failed'); }
  };

  const isCheckedIn = todayRecord?.check_in && !todayRecord?.check_out;
  const isCheckedOut = todayRecord?.check_out;

  return (
    <div data-testid="daily-attendance-section">
      <div className="bg-white border border-blue-100 rounded-lg shadow-sm p-6 mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-4">Today's Status</p>
        <div className="flex items-center gap-6">
          <div className="flex-1 grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Clock size={24} className="mx-auto mb-2 text-blue-600" />
              <p className="text-xs text-slate-500 uppercase">Check In</p>
              <p className="text-lg font-bold text-blue-900" data-testid="check-in-time">{todayRecord?.check_in ? new Date(todayRecord.check_in).toLocaleTimeString() : '-'}</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Clock size={24} className="mx-auto mb-2 text-indigo-600" />
              <p className="text-xs text-slate-500 uppercase">Check Out</p>
              <p className="text-lg font-bold text-blue-900" data-testid="check-out-time">{todayRecord?.check_out ? new Date(todayRecord.check_out).toLocaleTimeString() : '-'}</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Calendar size={24} className="mx-auto mb-2 text-emerald-600" />
              <p className="text-xs text-slate-500 uppercase">Hours</p>
              <p className="text-lg font-bold text-blue-900" data-testid="hours-worked">{todayRecord?.hours_worked ? `${todayRecord.hours_worked}h` : '-'}</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {!isCheckedIn && !isCheckedOut && <Button data-testid="check-in-btn" onClick={handleCheckIn} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold px-6"><LogIn size={16} className="mr-2" />Check In</Button>}
            {isCheckedIn && <Button data-testid="check-out-btn" onClick={handleCheckOut} className="bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold px-6"><LogOut size={16} className="mr-2" />Check Out</Button>}
            {isCheckedOut && <span className="text-xs text-emerald-600 font-bold uppercase text-center" data-testid="attendance-complete">Day Complete</span>}
          </div>
        </div>
      </div>

      <div className="bg-white border border-blue-100 rounded-lg shadow-sm p-4 mb-4 flex flex-wrap items-end gap-4">
        {isManagement && (
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Employee</Label>
            <select data-testid="filter-attendance-user" value={filterUser} onChange={e => setFilterUser(e.target.value)} className="mt-1 flex h-9 rounded-lg border border-blue-200 bg-white px-3 py-1 text-sm sr-only">
              <option value="">All Employees</option>
              {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <div className="mt-1">
              <SearchableSelect value={filterUser} onChange={setFilterUser}
                items={allUsers} emptyLabel="All Employees" placeholder="Search employee…" testId="filter-attendance-user-search"
                allowClear noneLabel="— All Employees —" />
            </div>
          </div>
        )}
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From</Label>
          <Input data-testid="filter-att-from" type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="mt-1 h-9 rounded-lg border-blue-200 text-sm" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To</Label>
          <Input data-testid="filter-att-to" type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="mt-1 h-9 rounded-lg border-blue-200 text-sm" />
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setFilterUser(''); setFilterDateFrom(''); setFilterDateTo(''); setSearchText(''); }} className="text-slate-500 text-xs">Clear</Button>
        {isManagement && (
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Search</Label>
            <Input data-testid="attendance-search-input" placeholder="Employee name or status…" value={searchText} onChange={e => setSearchText(e.target.value)} className="mt-1 h-9 rounded-lg border-blue-200 text-sm" />
          </div>
        )}
        <span className="text-xs text-slate-500 ml-auto">{records.length} records</span>
      </div>

      <div className="bg-white border border-blue-100 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              {isManagement && <TableHead className="font-bold text-blue-900">Employee</TableHead>}
              <TableHead className="font-bold text-blue-900">Date</TableHead>
              <TableHead className="font-bold text-blue-900">Check In</TableHead>
              <TableHead className="font-bold text-blue-900">Check Out</TableHead>
              <TableHead className="font-bold text-blue-900">Hours</TableHead>
              <TableHead className="font-bold text-blue-900">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              const q = searchText.trim().toLowerCase();
              const filteredRecs = q ? records.filter(r =>
                (r.user_name || '').toLowerCase().includes(q) ||
                (r.status || '').toLowerCase().includes(q) ||
                (r.date || '').toLowerCase().includes(q)
              ) : records;
              return filteredRecs.length === 0 ? (
              <TableRow><TableCell colSpan={isManagement ? 6 : 5} className="text-center text-slate-500 py-8">{q ? 'No records match your search.' : 'No records'}</TableCell></TableRow>
            ) : filteredRecs.map((r, i) => (
              <TableRow key={r.id || `${r.date}-${r.user_id || i}`} data-testid={`attendance-row-${i}`} className="hover:bg-blue-50/50">
                {isManagement && <TableCell className="font-semibold text-blue-900">{r.user_name}</TableCell>}
                <TableCell className="font-medium">{r.date}</TableCell>
                <TableCell className="text-sm">{r.check_in ? new Date(r.check_in).toLocaleTimeString() : '-'}</TableCell>
                <TableCell className="text-sm">{r.check_out ? new Date(r.check_out).toLocaleTimeString() : '-'}</TableCell>
                <TableCell className="font-bold text-blue-800">{r.hours_worked ? `${r.hours_worked}h` : '-'}</TableCell>
                <TableCell><span className={`px-2 py-1 text-xs font-bold uppercase rounded ${r.status === 'present' ? 'bg-emerald-100 text-emerald-800' : r.regularized ? 'bg-sky-100 text-sky-800' : 'bg-slate-100 text-slate-600'}`}>{r.regularized ? 'Regularized' : r.status}</span></TableCell>
              </TableRow>
            ));
            })()}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ==================== REGULARIZATION ==================== */
function Regularization({ getHeaders, isManagement, user }) {
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: '', check_in: '', check_out: '', reason: '' });

  const fetchReqs = useCallback(async () => {
    try { const res = await axios.get(`${API}/hrms/attendance/regularizations`, { headers: getHeaders() }); setRequests(res.data); } catch (e) { toast.error('Failed to load regularizations'); }
  }, []);

  useEffect(() => { fetchReqs(); }, []);

  const handleSubmit = async () => {
    if (!form.date || !form.reason) { toast.error('Date and reason are required'); return; }
    try {
      await axios.post(`${API}/hrms/attendance/regularize`, form, { headers: getHeaders() });
      toast.success('Regularization request submitted');
      setShowForm(false); setForm({ date: '', check_in: '', check_out: '', reason: '' }); fetchReqs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
  };

  const handleApprove = async (reqId, action) => {
    try {
      await axios.put(`${API}/hrms/attendance/regularizations/${reqId}/approve`, { action }, { headers: getHeaders() });
      toast.success(`Request ${action}`); fetchReqs();
    } catch (e) { toast.error('Failed to update'); }
  };

  return (
    <div data-testid="regularization-section">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm font-bold text-blue-900">Attendance Regularization Requests</p>
        <Button data-testid="new-regularization-btn" onClick={() => setShowForm(true)} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs"><Plus size={14} className="mr-1" />New Request</Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Request Regularization</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Date</Label><Input data-testid="reg-date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label className="text-xs font-bold">Check In Time</Label><Input data-testid="reg-checkin" type="time" value={form.check_in} onChange={e => setForm({ ...form, check_in: e.target.value })} /></div>
            <div><Label className="text-xs font-bold">Check Out Time</Label><Input data-testid="reg-checkout" type="time" value={form.check_out} onChange={e => setForm({ ...form, check_out: e.target.value })} /></div>
            <div><Label className="text-xs font-bold">Reason</Label><Textarea data-testid="reg-reason" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Why do you need regularization?" /></div>
            <Button data-testid="reg-submit-btn" onClick={handleSubmit} className="w-full bg-blue-700 hover:bg-blue-800 text-white">Submit Request</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-blue-100 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50">
              <TableHead className="font-bold text-blue-900">Employee</TableHead>
              <TableHead className="font-bold text-blue-900">Date</TableHead>
              <TableHead className="font-bold text-blue-900">Check In</TableHead>
              <TableHead className="font-bold text-blue-900">Check Out</TableHead>
              <TableHead className="font-bold text-blue-900">Reason</TableHead>
              <TableHead className="font-bold text-blue-900">Status</TableHead>
              {isManagement && <TableHead className="font-bold text-blue-900">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow><TableCell colSpan={isManagement ? 7 : 6} className="text-center text-slate-500 py-8">No regularization requests</TableCell></TableRow>
            ) : requests.map((r, i) => (
              <TableRow key={r.id || i} data-testid={`reg-row-${i}`}>
                <TableCell className="font-semibold text-blue-900">{r.user_name}</TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell>{r.check_in || '-'}</TableCell>
                <TableCell>{r.check_out || '-'}</TableCell>
                <TableCell className="text-xs max-w-[200px] truncate">{r.reason}</TableCell>
                <TableCell><span className={`px-2 py-1 text-xs font-bold uppercase rounded ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : r.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{r.status}</span></TableCell>
                {isManagement && (
                  <TableCell>
                    {r.status === 'pending' && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" data-testid={`reg-approve-${i}`} onClick={() => handleApprove(r.id, 'approved')} className="text-emerald-600 h-7 text-xs"><Check size={14} /></Button>
                        <Button size="sm" variant="ghost" data-testid={`reg-reject-${i}`} onClick={() => handleApprove(r.id, 'rejected')} className="text-red-600 h-7 text-xs"><X size={14} /></Button>
                      </div>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ==================== TEAM VIEW ==================== */
function TeamView({ getHeaders, user }) {
  const [data, setData] = useState({ members: [], records: [] });
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hrms/attendance/team?date=${date}`, { headers: getHeaders() });
      setData(res.data);
    } catch (e) { toast.error('Failed to load team attendance'); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchTeam(); }, [date]);

  return (
    <div data-testid="team-view-section">
      <div className="flex items-center gap-4 mb-4">
        <div>
          <Label className="text-xs font-bold uppercase text-blue-900">Date</Label>
          <Input data-testid="team-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="h-9 rounded-lg border-blue-200 text-sm" />
        </div>
        <span className="text-xs text-slate-500 mt-5">{data.members.length} team members</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={32} className="animate-spin text-blue-400" /></div>
      ) : (
        <div className="bg-white border border-blue-100 rounded-lg shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-50">
                <TableHead className="font-bold text-blue-900">Employee</TableHead>
                <TableHead className="font-bold text-blue-900">Check In</TableHead>
                <TableHead className="font-bold text-blue-900">Check Out</TableHead>
                <TableHead className="font-bold text-blue-900">Hours</TableHead>
                <TableHead className="font-bold text-blue-900">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.members.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No team members</TableCell></TableRow>
              ) : data.members.map((m, i) => {
                const rec = data.records.find(r => r.user_id === m.id);
                return (
                  <TableRow key={m.id} data-testid={`team-row-${i}`}>
                    <TableCell className="font-semibold text-blue-900">{m.name}</TableCell>
                    <TableCell className="text-sm">{rec?.check_in_time || rec?.check_in ? new Date(rec.check_in_time || rec.check_in).toLocaleTimeString() : '-'}</TableCell>
                    <TableCell className="text-sm">{rec?.check_out_time || rec?.check_out ? new Date(rec.check_out_time || rec.check_out).toLocaleTimeString() : '-'}</TableCell>
                    <TableCell className="font-bold text-blue-800">{rec?.hours_worked ? `${rec.hours_worked}h` : '-'}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${rec ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
                        {rec ? (rec.regularized ? 'Regularized' : 'Present') : 'Absent'}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/* ==================== HOLIDAYS ==================== */
function Holidays({ getHeaders, isAdmin }) {
  const [holidays, setHolidays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', type: 'public' });

  const fetchHolidays = useCallback(async () => {
    try { const res = await axios.get(`${API}/hrms/holidays`, { headers: getHeaders() }); setHolidays(res.data); } catch (e) { toast.error('Failed to load holidays'); }
  }, []);

  useEffect(() => { fetchHolidays(); }, []);

  const handleAdd = async () => {
    if (!form.name || !form.date) { toast.error('Name and date are required'); return; }
    try {
      await axios.post(`${API}/hrms/holidays`, form, { headers: getHeaders() });
      toast.success('Holiday added'); setShowForm(false); setForm({ name: '', date: '', type: 'public' }); fetchHolidays();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add holiday'); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/hrms/holidays/${id}`, { headers: getHeaders() }); toast.success('Holiday deleted'); fetchHolidays(); } catch (e) { toast.error('Failed to delete'); }
  };

  const now = new Date();
  const upcoming = holidays.filter(h => new Date(h.date) >= now);
  const past = holidays.filter(h => new Date(h.date) < now);

  return (
    <div data-testid="holidays-section">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm font-bold text-blue-900">Holidays Calendar</p>
        {isAdmin && <Button data-testid="add-holiday-btn" onClick={() => setShowForm(true)} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs"><Plus size={14} className="mr-1" />Add Holiday</Button>}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Holiday</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Holiday Name</Label><Input data-testid="holiday-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Republic Day" /></div>
            <div><Label className="text-xs font-bold">Date</Label><Input data-testid="holiday-date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <Label className="text-xs font-bold">Type</Label>
              <select data-testid="holiday-type" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="flex h-9 w-full rounded-lg border border-slate-200 bg-white px-3 py-1 text-sm">
                <option value="public">Public Holiday</option>
                <option value="restricted">Restricted Holiday</option>
                <option value="company">Company Holiday</option>
              </select>
            </div>
            <Button data-testid="holiday-submit-btn" onClick={handleAdd} className="w-full bg-blue-700 hover:bg-blue-800 text-white">Add Holiday</Button>
          </div>
        </DialogContent>
      </Dialog>

      {upcoming.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Upcoming Holidays</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {upcoming.map((h, i) => (
              <div key={h.id || `${h.date}-${h.name}`} data-testid={`holiday-upcoming-${i}`} className="bg-white border border-emerald-200 rounded-lg p-3 flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-50 rounded-lg flex flex-col items-center justify-center">
                  <span className="text-xs font-bold text-emerald-700">{new Date(h.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                  <span className="text-lg font-extrabold text-emerald-900">{new Date(h.date).getDate()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-blue-900 text-sm truncate">{h.name}</p>
                  <span className="text-[10px] uppercase font-bold text-slate-400">{h.type}</span>
                </div>
                {isAdmin && <Button variant="ghost" size="sm" onClick={() => handleDelete(h.id)} className="text-red-400 h-7"><Trash2 size={14} /></Button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Past Holidays</p>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-bold text-slate-600">Name</TableHead>
                  <TableHead className="font-bold text-slate-600">Date</TableHead>
                  <TableHead className="font-bold text-slate-600">Type</TableHead>
                  {isAdmin && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {past.map((h, i) => (
                  <TableRow key={h.id || `${h.date}-${h.name}`} data-testid={`holiday-past-${i}`}>
                    <TableCell className="font-medium">{h.name}</TableCell>
                    <TableCell>{h.date}</TableCell>
                    <TableCell><span className="text-xs uppercase font-bold text-slate-400">{h.type}</span></TableCell>
                    {isAdmin && <TableCell><Button variant="ghost" size="sm" onClick={() => handleDelete(h.id)} className="text-red-400 h-7"><Trash2 size={14} /></Button></TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {holidays.length === 0 && <p className="text-center text-slate-400 py-8">No holidays added yet</p>}
    </div>
  );
}

/* ==================== MONTHLY CALENDAR VIEW ==================== */
function MonthlyCalendar({ data, month }) {
  const calendarData = useMemo(() => {
    if (!data || !month) return null;
    const year = parseInt(month.slice(0, 4));
    const m = parseInt(month.slice(5, 7)) - 1;
    const daysInMonth = new Date(year, m + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, m, 1).getDay();
    const today = new Date().toISOString().slice(0, 10);

    const presentDates = new Set((data.attendance || []).map(a => a.date));
    const holidayMap = {};
    (data.holidays || []).forEach(h => { holidayMap[h.date] = h.name; });

    const leaveDates = {};
    (data.leaves || []).forEach(l => {
      const start = new Date(l.start_date);
      const end = new Date(l.end_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        leaveDates[d.toISOString().slice(0, 10)] = l.leave_type || 'Leave';
      }
    });

    const days = [];
    let absentCount = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(year, m, day);
      const dateStr = `${year}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayOfWeek = dateObj.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isFuture = dateStr > today;

      let status = 'future';
      let label = '';
      let tooltip = '';

      if (isWeekend) {
        status = 'weekend';
        label = dayOfWeek === 0 ? 'Sun' : 'Sat';
      } else if (holidayMap[dateStr]) {
        status = 'holiday';
        tooltip = holidayMap[dateStr];
      } else if (leaveDates[dateStr]) {
        status = 'leave';
        tooltip = leaveDates[dateStr];
      } else if (presentDates.has(dateStr)) {
        status = 'present';
      } else if (!isFuture) {
        status = 'absent';
        absentCount++;
      }

      days.push({ day, dateStr, status, label, tooltip, isWeekend, isFuture });
    }

    return { days, firstDayOfWeek, absentCount, presentCount: presentDates.size, leaveCount: Object.keys(leaveDates).length, holidayCount: (data.holidays || []).length };
  }, [data, month]);

  if (!calendarData) return null;

  const statusStyles = {
    present: 'bg-emerald-500 text-white font-bold',
    absent: 'bg-red-400 text-white font-bold',
    leave: 'bg-amber-400 text-white font-bold',
    holiday: 'bg-blue-500 text-white font-bold',
    weekend: 'bg-slate-100 text-slate-400',
    future: 'bg-white text-slate-300 border border-dashed border-slate-200',
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white border border-blue-100 rounded-lg p-5 mb-4" data-testid="monthly-calendar">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-blue-900">Monthly Calendar View</p>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" />Present</span>
          <span className="flex items-center gap-1.5 text-[11px] font-semibold"><span className="w-3 h-3 rounded-sm bg-red-400 inline-block" />Absent</span>
          <span className="flex items-center gap-1.5 text-[11px] font-semibold"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" />Leave</span>
          <span className="flex items-center gap-1.5 text-[11px] font-semibold"><span className="w-3 h-3 rounded-sm bg-blue-500 inline-block" />Holiday</span>
          <span className="flex items-center gap-1.5 text-[11px] font-semibold"><span className="w-3 h-3 rounded-sm bg-slate-100 border border-slate-200 inline-block" />Weekend</span>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {weekDays.map(d => (
          <div key={d} className="text-center text-[10px] font-bold uppercase tracking-wider text-slate-500 py-1">{d}</div>
        ))}
        {Array.from({ length: calendarData.firstDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {calendarData.days.map(({ day, dateStr, status, tooltip }) => (
          <div
            key={dateStr}
            data-testid={`cal-day-${dateStr}`}
            title={tooltip || status}
            className={`relative h-10 rounded-lg flex items-center justify-center text-sm transition-all cursor-default ${statusStyles[status]} ${status !== 'weekend' && status !== 'future' ? 'shadow-sm' : ''}`}
          >
            {day}
            {tooltip && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-white border border-current" />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
        <span className="text-xs text-slate-600" data-testid="cal-summary-present"><span className="font-bold text-emerald-700">{calendarData.presentCount}</span> Present</span>
        <span className="text-xs text-slate-600" data-testid="cal-summary-absent"><span className="font-bold text-red-600">{calendarData.absentCount}</span> Absent</span>
        <span className="text-xs text-slate-600"><span className="font-bold text-amber-600">{calendarData.leaveCount}</span> Leave</span>
        <span className="text-xs text-slate-600"><span className="font-bold text-blue-600">{calendarData.holidayCount}</span> Holiday</span>
      </div>
    </div>
  );
}

/* ==================== EMPLOYEE RECORDS ==================== */
function EmployeeRecords({ getHeaders, user, isManagement }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedUser, setSelectedUser] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isManagement) {
      axios.get(`${API}/users`, { headers: getHeaders() }).then(r => setAllUsers(r.data)).catch(() => {});
    }
  }, [isManagement]);

  const fetchRecords = useCallback(async () => {
    const uid = selectedUser || user?.id;
    if (!uid) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hrms/employee-records/${uid}?period=${period}&month=${month}`, { headers: getHeaders() });
      setData(res.data);
    } catch (e) { toast.error('Failed to load records'); }
    finally { setLoading(false); }
  }, [selectedUser, period, month, user]);

  useEffect(() => { if (user) fetchRecords(); }, [user, selectedUser, period, month]);

  return (
    <div data-testid="employee-records-section">
      <div className="flex flex-wrap items-end gap-4 mb-4">
        {isManagement && (
          <div>
            <Label className="text-xs font-bold uppercase text-blue-900">Employee</Label>
            <select data-testid="records-user" value={selectedUser} onChange={e => setSelectedUser(e.target.value)} className="flex h-9 rounded-lg border border-blue-200 bg-white px-3 py-1 text-sm mt-1 sr-only">
              <option value="">Myself</option>
              {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <div className="mt-1">
              <SearchableSelect value={selectedUser} onChange={setSelectedUser}
                items={allUsers} emptyLabel="Myself" placeholder="Search employee…" testId="records-user-search"
                allowClear noneLabel="— Myself —" />
            </div>
          </div>
        )}
        <div>
          <Label className="text-xs font-bold uppercase text-blue-900">Period</Label>
          <select data-testid="records-period" value={period} onChange={e => setPeriod(e.target.value)} className="flex h-9 rounded-lg border border-blue-200 bg-white px-3 py-1 text-sm mt-1">
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        {period === 'monthly' && (
          <div>
            <Label className="text-xs font-bold uppercase text-blue-900">Month</Label>
            <Input data-testid="records-month" type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-9 rounded-lg border-blue-200 text-sm mt-1" />
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={32} className="animate-spin text-blue-400" /></div>
      ) : data ? (
        <div>
          <div className="bg-white border border-blue-100 rounded-lg p-4 mb-4">
            <p className="text-sm font-bold text-blue-900 mb-1">{data.user_name}</p>
            <p className="text-xs text-slate-500">{data.period === 'weekly' ? 'This Week' : `${data.start} to ${data.end}`}</p>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-extrabold text-emerald-700">{data.attendance?.length || 0}</p>
                <p className="text-xs font-bold text-emerald-600 uppercase">Days Present</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-extrabold text-amber-700">{data.leaves?.length || 0}</p>
                <p className="text-xs font-bold text-amber-600 uppercase">Leaves</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-extrabold text-blue-700">{data.holidays?.length || 0}</p>
                <p className="text-xs font-bold text-blue-600 uppercase">Holidays</p>
              </div>
            </div>
          </div>

          {period === 'monthly' && <MonthlyCalendar data={data} month={month} />}

          <div className="bg-white border border-blue-100 rounded-lg shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-blue-50">
                  <TableHead className="font-bold text-blue-900">Date</TableHead>
                  <TableHead className="font-bold text-blue-900">Check In</TableHead>
                  <TableHead className="font-bold text-blue-900">Check Out</TableHead>
                  <TableHead className="font-bold text-blue-900">Hours</TableHead>
                  <TableHead className="font-bold text-blue-900">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.attendance || []).length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">No attendance records for this period</TableCell></TableRow>
                ) : (data.attendance || []).map((a, i) => (
                  <TableRow key={a.id || `${a.date}-${i}`} data-testid={`record-row-${i}`}>
                    <TableCell className="font-medium">{a.date}</TableCell>
                    <TableCell className="text-sm">{a.check_in_time || a.check_in ? new Date(a.check_in_time || a.check_in).toLocaleTimeString() : '-'}</TableCell>
                    <TableCell className="text-sm">{a.check_out_time || a.check_out ? new Date(a.check_out_time || a.check_out).toLocaleTimeString() : '-'}</TableCell>
                    <TableCell className="font-bold text-blue-800">{a.hours_worked ? `${a.hours_worked}h` : '-'}</TableCell>
                    <TableCell><span className="px-2 py-1 text-xs font-bold uppercase rounded bg-emerald-100 text-emerald-800">{a.regularized ? 'Regularized' : 'Present'}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {(data.holidays || []).length > 0 && (
            <div className="mt-4 bg-white border border-blue-100 rounded-lg p-4">
              <p className="text-xs font-bold uppercase text-blue-900 mb-2">Holidays in Period</p>
              <div className="flex flex-wrap gap-2">
                {data.holidays.map((h) => (
                  <span key={h.id || `${h.date}-${h.name}`} className="px-3 py-1 bg-blue-50 border border-blue-200 rounded-lg text-xs font-semibold text-blue-800">{h.name} ({h.date})</span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* ==================== COMP OFF ==================== */
function CompOff({ getHeaders, isManagement, user }) {
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: '', hours_worked: 'full', work_description: '' });

  const fetchReqs = useCallback(async () => {
    try { const res = await axios.get(`${API}/hrms/comp-off`, { headers: getHeaders() }); setRequests(res.data); } catch (e) { toast.error('Failed to load comp-off requests'); }
  }, []);

  useEffect(() => { fetchReqs(); }, []);

  const handleSubmit = async () => {
    if (!form.date || !form.work_description) { toast.error('Date and description required'); return; }
    try {
      await axios.post(`${API}/hrms/comp-off`, form, { headers: getHeaders() });
      toast.success('Comp-off request submitted'); setShowForm(false); setForm({ date: '', hours_worked: 'full', work_description: '' }); fetchReqs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
  };

  const handleApprove = async (reqId, action) => {
    try {
      await axios.put(`${API}/hrms/comp-off/${reqId}/approve`, { action }, { headers: getHeaders() });
      toast.success(`Comp-off ${action}`); fetchReqs();
    } catch (e) { toast.error('Failed to update'); }
  };

  return (
    <div data-testid="compoff-section">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm font-bold text-blue-900">Comp-Off Requests</p>
        <Button data-testid="new-compoff-btn" onClick={() => setShowForm(true)} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs"><Plus size={14} className="mr-1" />Request Comp Off</Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Request Comp Off</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Date Worked (Holiday/Weekend)</Label><Input data-testid="compoff-date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <Label className="text-xs font-bold">Hours Worked</Label>
              <select data-testid="compoff-hours" value={form.hours_worked} onChange={e => setForm({ ...form, hours_worked: e.target.value })} className="flex h-9 w-full rounded-lg border border-slate-200 bg-white px-3 py-1 text-sm">
                <option value="full">Full Day</option>
                <option value="half">Half Day</option>
              </select>
            </div>
            <div><Label className="text-xs font-bold">Work Description</Label><Textarea data-testid="compoff-desc" value={form.work_description} onChange={e => setForm({ ...form, work_description: e.target.value })} placeholder="What work did you do?" /></div>
            <Button data-testid="compoff-submit-btn" onClick={handleSubmit} className="w-full bg-blue-700 hover:bg-blue-800 text-white">Submit Request</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-blue-100 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50">
              <TableHead className="font-bold text-blue-900">Employee</TableHead>
              <TableHead className="font-bold text-blue-900">Date</TableHead>
              <TableHead className="font-bold text-blue-900">Duration</TableHead>
              <TableHead className="font-bold text-blue-900">Description</TableHead>
              <TableHead className="font-bold text-blue-900">Status</TableHead>
              {isManagement && <TableHead className="font-bold text-blue-900">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow><TableCell colSpan={isManagement ? 6 : 5} className="text-center text-slate-500 py-8">No comp-off requests</TableCell></TableRow>
            ) : requests.map((r, i) => (
              <TableRow key={r.id || i} data-testid={`compoff-row-${i}`}>
                <TableCell className="font-semibold text-blue-900">{r.user_name}</TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell className="text-xs uppercase font-bold">{r.hours_worked === 'full' ? 'Full Day' : 'Half Day'}</TableCell>
                <TableCell className="text-xs max-w-[200px] truncate">{r.work_description}</TableCell>
                <TableCell><span className={`px-2 py-1 text-xs font-bold uppercase rounded ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : r.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{r.status}</span></TableCell>
                {isManagement && (
                  <TableCell>
                    {r.status === 'pending' && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" data-testid={`compoff-approve-${i}`} onClick={() => handleApprove(r.id, 'approved')} className="text-emerald-600 h-7"><Check size={14} /></Button>
                        <Button size="sm" variant="ghost" data-testid={`compoff-reject-${i}`} onClick={() => handleApprove(r.id, 'rejected')} className="text-red-600 h-7"><X size={14} /></Button>
                      </div>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
